import appVariables from '@/config'
import { getCloudflareImageUrl } from '@fusion/media/cloudflare'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ReplayIcon from '@mui/icons-material/Replay'
import {
  Avatar,
  AvatarGroup,
  Box,
  Card,
  CardHeader,
  Collapse,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'

import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'

import OrderAgainItemCard, {
  ORDER_AGAIN_CARD_WIDTH,
  OrderAgainItemCardData,
} from './OrderAgainItemCard'

const MAX_THUMBNAILS = 4
const STATIC_ROW_MAX = 3

export type OrderAgainSectionItem = OrderAgainItemCardData & {
  vendorId: string
  vendorName: string
  vendorLogo: string | null
}

type Props = {
  items: OrderAgainSectionItem[]
  hidePrices: boolean
  disabledItemReferences?: Set<string>
  onAddToCart: (itemReference: string) => void
  onShowOptions: (itemReference: string) => void
}

const OrderAgainSection: React.FC<Props> = ({
  items,
  hidePrices,
  disabledItemReferences,
  onAddToCart,
  onShowOptions,
}) => {
  const { t } = useTranslation()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  const [expanded, setExpanded] = useState(false)

  if (items.length === 0) return null

  const distinctVendors = new Set(items.map((item) => item.vendorId))
  const isMultiVendor = distinctVendors.size > 1
  const showBadge = items.length > 1 && !isMobile
  const isScrollable = items.length > STATIC_ROW_MAX

  const visibleThumbnails = items.slice(0, MAX_THUMBNAILS)
  const overflowCount = items.length - visibleThumbnails.length

  const subtitle = isMultiVendor
    ? t('cc:orderAgain.subtitleMultiVendor')
    : t('cc:orderAgain.subtitleSingleVendor', { vendor: items[0].vendorName })

  return (
    <Card sx={{ mb: 1 }}>
      <CardHeader
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onClick={() => setExpanded((prev) => !prev)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            setExpanded((prev) => !prev)
          }
        }}
        sx={{
          padding: '10px',
          alignItems: 'center',
          '&:hover': { bgcolor: 'action.hover' },
          '& .MuiCardHeader-action': {
            alignSelf: 'center',
            margin: 0,
          },
        }}
        title={
          <Stack direction="row" alignItems="center" gap={1.25}>
            {!isMobile && (
              <Box
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: 1.5,
                  bgcolor: 'primary.main',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                <ReplayIcon sx={{ width: 16, height: 16, color: 'primary.contrastText' }} />
              </Box>
            )}
            <Typography variant="body1" fontWeight={700} noWrap>
              {t('cc:orderAgain.title')}
            </Typography>
            {showBadge ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  bgcolor: 'action.hover',
                  borderRadius: 10,
                  padding: '2px 8px',
                }}>
                <Typography
                  variant="caption"
                  fontWeight={600}
                  lineHeight={1}
                  sx={{ color: 'text.secondary' }}>
                  {t('cc:orderAgain.itemCount', { count: items.length })}
                </Typography>
              </Box>
            ) : null}
          </Stack>
        }
        action={
          <Stack direction="row" alignItems="center" gap={1.5}>
            <AvatarGroup
              max={MAX_THUMBNAILS + 1}
              sx={{ '& .MuiAvatar-root': { width: 28, height: 28, fontSize: 11 } }}>
              {visibleThumbnails.map((item) => (
                <Avatar
                  key={item.itemReference}
                  alt={item.name ?? ''}
                  src={
                    item.image
                      ? getCloudflareImageUrl({
                          enabled: appVariables.CLOUDFLARE_OPTIMISE_IMAGE,
                          src: item.image,
                          width: 100,
                          quality: 100,
                        })
                      : undefined
                  }
                />
              ))}
              {overflowCount > 0 ? <Avatar>{`+${overflowCount}`}</Avatar> : null}
            </AvatarGroup>
            <Box
              sx={{
                width: 26,
                height: 26,
                borderRadius: '50%',
                bgcolor: 'action.hover',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              <ExpandMoreIcon
                sx={{
                  width: 18,
                  height: 18,
                  transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.2s',
                  color: 'text.secondary',
                }}
              />
            </Box>
          </Stack>
        }
      />
      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ px: 2, pb: 2, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Typography variant="body2" color="text.secondary" sx={{ pb: 1.25 }}>
            {subtitle}
          </Typography>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              ...(isScrollable
                ? {
                    overflowX: 'auto',
                    pb: 1,
                    scrollbarWidth: 'thin',
                    '&::-webkit-scrollbar': { height: 6 },
                    '&::-webkit-scrollbar-track': { bgcolor: 'transparent' },
                    '&::-webkit-scrollbar-thumb': { borderRadius: 3, bgcolor: 'divider' },
                  }
                : { flexWrap: 'wrap' }),
            }}>
            {items.map((item) => (
              <Box key={item.itemReference} sx={{ width: ORDER_AGAIN_CARD_WIDTH, flexShrink: 0 }}>
                <OrderAgainItemCard
                  itemData={item}
                  vendorName={item.vendorName}
                  vendorLogo={item.vendorLogo}
                  hidePrices={hidePrices}
                  disableAddToCart={disabledItemReferences?.has(item.itemReference)}
                  onAddToCart={onAddToCart}
                  onShowOptions={onShowOptions}
                />
              </Box>
            ))}
          </Box>
        </Box>
      </Collapse>
    </Card>
  )
}

export default OrderAgainSection
